import { ServiceBase } from "@app/shared";
import { ProviderHubCountiesImpacted, ProviderHubFilterState, ProviderHubFilterValue, ProviderHubServicingProviders, RegionSetSpecialtyGroupsWithGaps, ServicingProviderAccessPoints } from "./interfaces";
import { clone } from "lodash";
import { PaginationResult } from "@app/shared/waffles/exports";
import { LocationConfidenceScoreLabelEnum, ProviderNetworkType } from "@app/projects/enumerators";
import { BasicOperatorType } from "@app/exports/enumerations";
import { DropdownItem } from "@app/shared/checkbox-dropdown";

export default class ProviderHubService extends ServiceBase {

  /**
   * Get the service areas and specialties with gaps. So far will display mocked data.
   */
  public async getRegionSetSpecialtyGroupsWithGaps(): Promise<RegionSetSpecialtyGroupsWithGaps[]> {

    await new Promise( res => setTimeout(res, 1000) ); // 1 Second delay
    return mockRegionSetSpecialtyGroupsWithGaps;

  }

  /**
   * Get the providers that can impact any of the county/specialties in the gap table. So far will display mocked data
   */
  public async getProviderHubServicingProviders(pageIndex: number, pageSize: number, filters: ProviderHubFilterState): Promise<PaginationResult<ProviderHubServicingProviders>> {

    //Mimic the filtering functionality against the mocked data
    const filteredData = await this.getFilteredServicingProviders(filters);

    await new Promise( res => setTimeout(res, 1000) ); // 1 Second delay

    // Mimic the backend pagination against the mocked data
    const paginatedData: PaginationResult<ProviderHubServicingProviders> = {
      items: filteredData.slice((pageIndex - 1) * pageSize, pageIndex * pageSize),
      totalCount: filteredData.length
    }

    return paginatedData;
   
  }

  /* Used so far to perform actual filtering against the mocked data and to be able to demo the functionality. Would probably be removed once we have live data */
  private async getFilteredServicingProviders(filters: ProviderHubFilterState): Promise<ProviderHubServicingProviders[]> {

    const providersData = clone(mockedProviderHubServicingProviders);

    const filteredData = providersData.filter((provider) => {
      
      return Object.entries(filters).every(([columnName, {operator, value}]) => {
        
        const rowValue = provider[columnName as keyof ProviderHubServicingProviders];

        if (value === '') return true;

        // For operator-based filters, like strings or numbers
        if (operator) {
          switch (operator) {
            case BasicOperatorType.Equal: 
              return String(rowValue) === value;
            case BasicOperatorType.NotEqual: 
              return String(rowValue) !== value;
            case BasicOperatorType.Contains: 
              return String(rowValue).toLowerCase().includes(value.toString().toLowerCase());
            case BasicOperatorType.Greater: 
              return Number(rowValue) > Number(value);
            case BasicOperatorType.GreaterEqual: 
              return Number(rowValue) >= Number(value);
            case BasicOperatorType.Less: 
              return Number(rowValue) < Number(value);
            case BasicOperatorType.LessEqual: 
              return Number(rowValue) <= Number(value);
            default: 
              return true;
          }
        }
         
        // For multiselect dropdown-only filters
        const selectedKeys = this.toValueArray(value);

        // For the multiselect nested dropdown (Counties Impacted)
        if (columnName == 'countiesImpacted' && Array.isArray(rowValue)) {
          const countyKeys = (rowValue as ProviderHubCountiesImpacted[]).map((county) => county.key);
          return countyKeys.some((countyKey) => selectedKeys.includes(countyKey));
        }
  
        // For the other type of multiselect dropdowns (not nested ones)
        return this.matchesAny(rowValue, selectedKeys);

      })

    });

    return filteredData;
  }

  /**
   * Get the access points associated to the given servicing provider ID
   */
  public async getAccessPointsByServicingProviders(servicingProviderId: number): Promise<ServicingProviderAccessPoints[]>{

    await new Promise( res => setTimeout(res, 1000) ); // 1 Second delay
    return mockAccessPointsByServicingProviders.get(servicingProviderId);

  }

  /* Used so far to perform actual filtering against the mocked data and to be able to demo the functionality. Would probably be removed once we have live data */
  private toValueArray(value: ProviderHubFilterValue['value']): string[] {
    if (Array.isArray(value)) {
      // value could be string[] (for MultiselectNestedDropdowns) OR DropdownItem<string>[] for MultiselectDropdowns
      if (value.length === 0) return [];
      return typeof value[0] === 'string'
        ? (value as string[]) 
        : (value as DropdownItem<string>[]).map((d) =>  String(d.key));
    }
    // single string
    return [value as string];
  }

  /* Used so far to perform actual filtering against the mocked data and to be able to demo the functionality. Would probably be removed once we have live data */
  private matchesAny(rowValue: unknown, selected: string[]): boolean {

    if (selected.length === 0) return true;

    if (Array.isArray(rowValue)) {
      return rowValue.some((item) => {
        if (typeof item === 'object' && item !== null && 'key' in item) {
          return selected.includes(String(item.key));
        }
        return selected.includes(String(item));
      });
    }

    return selected.includes(String(rowValue));
  }
}

/* eslint-disable sort-keys */
const mockRegionSetSpecialtyGroupsWithGaps: RegionSetSpecialtyGroupsWithGaps[] = [
  {regionSetSpecialtyGroupId: 1, serviceArea: "Johnson, KS", specialtyGroup: "Primary Care", servicingProviderIdsThatCloseGaps: [1, 2, 3]},
  {regionSetSpecialtyGroupId: 2, serviceArea: "Miami, FL", specialtyGroup: "Dermatollogy", servicingProviderIdsThatCloseGaps: [2]},
  {regionSetSpecialtyGroupId: 3, serviceArea: "Johnson, KS", specialtyGroup: "Allergy and Inmunology", servicingProviderIdsThatCloseGaps: [1, 6]},
  {regionSetSpecialtyGroupId: 4, serviceArea: "Cook, KS", specialtyGroup: "Critical Care Services – Intensive Care Units (ICU)", servicingProviderIdsThatCloseGaps: [1, 2, 4, 5, 6]},
  {regionSetSpecialtyGroupId: 5, serviceArea: "Buttler, KS", specialtyGroup: "General Surgery", servicingProviderIdsThatCloseGaps: [1]},
  {regionSetSpecialtyGroupId: 6, serviceArea: "Ellis, MO", specialtyGroup: "Diagnostic Radiology", servicingProviderIdsThatCloseGaps: [5, 6, 12, 13, 21]},
  {regionSetSpecialtyGroupId: 7, serviceArea: "Cook, KS", specialtyGroup: "Acute Inpatient Hospitals", servicingProviderIdsThatCloseGaps: [3, 5, 12, 13, 21]},
  {regionSetSpecialtyGroupId: 8, serviceArea: "Ellis, MO", specialtyGroup: "Cardiac Surgery Program", servicingProviderIdsThatCloseGaps: [2, 4, 8, 10, 12, 14]},
  {regionSetSpecialtyGroupId: 9, serviceArea: "Los Angeles, CA", specialtyGroup: "Cardiology", servicingProviderIdsThatCloseGaps: [3, 5, 7, 9, 11]},
  {regionSetSpecialtyGroupId: 10, serviceArea: "Mayne, MI", specialtyGroup: "Endocrinology", servicingProviderIdsThatCloseGaps: [10]},
  {regionSetSpecialtyGroupId: 11, serviceArea: "Queens, NY", specialtyGroup: "Gastroenterology",  servicingProviderIdsThatCloseGaps: [5, 10, 15, 20]},
  {regionSetSpecialtyGroupId: 12, serviceArea: "Johnson, KS", specialtyGroup: "Gynecology, OB/GYN", servicingProviderIdsThatCloseGaps: [21]},
  {regionSetSpecialtyGroupId: 13, serviceArea: "Allen, KS", specialtyGroup: "Nephrology", servicingProviderIdsThatCloseGaps: [2, 4, 6]},
  {regionSetSpecialtyGroupId: 14, serviceArea: "Ellis, MO", specialtyGroup: "Occupational Therapy", servicingProviderIdsThatCloseGaps: [3, 6, 9]},
  {regionSetSpecialtyGroupId: 15, serviceArea: "Buttler, KS", specialtyGroup: "Oncology, Medical, Surgical", servicingProviderIdsThatCloseGaps: [2]},
  {regionSetSpecialtyGroupId: 16, serviceArea: "Allen, KS", specialtyGroup: "Speect Terapy", servicingProviderIdsThatCloseGaps: [16, 17, 18]},
  {regionSetSpecialtyGroupId: 17, serviceArea: "Los Angeles, CA", specialtyGroup: "Vascular Surgery", servicingProviderIdsThatCloseGaps: [19]},
  {regionSetSpecialtyGroupId: 18, serviceArea: "Ellis, MO", specialtyGroup: "Urology", servicingProviderIdsThatCloseGaps: [3]},
  {regionSetSpecialtyGroupId: 19, serviceArea: "Mayne, MI", specialtyGroup: "Pulmonology", servicingProviderIdsThatCloseGaps: [5]},
  {regionSetSpecialtyGroupId: 20, serviceArea: "Mayne, MI", specialtyGroup: "Chiropractor", servicingProviderIdsThatCloseGaps: [9]}
];

const mockAccessPointsByServicingProviders: Map<number, ServicingProviderAccessPoints[]> = new Map([
  [1, [
        { 
          accessPointId: 100,
          networkType: ProviderNetworkType.Prospect,
          gapsClosed: 1, 
          specialtyImpacted: "Generaly Surgery", 
          countiesImpacted: [
            { key: "17000", countyName: "Allen", stateCode: "KS" },
            { key: "17020", countyName: "Atchison", stateCode: "KS" },
            { key: "17030", countyName: "Barber", stateCode: "KS" }
          ], 
          address: "3901 RAINBOW BLVD", 
          address2: "Address 2 Main Street", 
          state: "KS", 
          city: "OLATHE", 
          zip: "66061", 
          locationConfidence: LocationConfidenceScoreLabelEnum.High
        },
        { 
          accessPointId: 101,
          networkType: ProviderNetworkType.NetNew,
          gapsClosed: 0, 
          specialtyImpacted: "Cardiology", 
          countiesImpacted: [
            { key: "17030", countyName: "Barber", stateCode: "KS" }
          ], 
          address: "1403 W 29 S", 
          state: "KS", 
          city: "WINFIELD", 
          zip: "67156", 
          locationConfidence: LocationConfidenceScoreLabelEnum.Medium
        },
        { 
          accessPointId: 102,
          networkType: ProviderNetworkType.Network,
          gapsClosed: 4, 
          specialtyImpacted: "Cardiology", 
          countiesImpacted: [
            { key: "17000", countyName: "Allen", stateCode: "KS" },
            { key: "17020", countyName: "Atchison", stateCode: "KS" },
          ], 
          address: "3625 QUAIL RIDGE RD", 
          state: "KS", 
          city: "LAWRENCE", 
          zip: "602354", 
          locationConfidence: LocationConfidenceScoreLabelEnum.Low
        }
      ]
  ],
  [2, [
        { 
          accessPointId: 103,
          networkType: ProviderNetworkType.Market,
          gapsClosed: 3, 
          specialtyImpacted: "General Surgery", 
          countiesImpacted: [
            { key: "17060", countyName: "Brown", stateCode: "KS" }
          ],
          address: "1023 KENTUCKY ST", 
          state: "KS", 
          city: "LAWRENCE", 
          zip: "66044", 
          locationConfidence: LocationConfidenceScoreLabelEnum.VeryLow
        }
      ]
  ],
  [3, [
        { 
          accessPointId: 104,
          networkType: ProviderNetworkType.Prospect,
          gapsClosed: 10, 
          specialtyImpacted: "Primary Care", 
          countiesImpacted: [
            { key: "17090", countyName: "Chautauqua", stateCode: "KS" },
            { key: "17100", countyName: "Cherokee", stateCode: "KS" }
          ], 
          address: "3901 RAINBOW BLVD", 
          address2: "Address 2 Main Street", 
          state: "KS", 
          city: "OLATHE", 
          zip: "66061", 
          locationConfidence: LocationConfidenceScoreLabelEnum.High
        },
        { 
          accessPointId: 105,
          networkType: ProviderNetworkType.Prospect,
          gapsClosed: 5, 
          specialtyImpacted: "Cardiology", 
          countiesImpacted: [
            { key: "17110", countyName: "Cheyenne", stateCode: "KS" }
          ], 
          address: "1403 W 29 S", 
          state: "KS", 
          city: "WINFIELD", 
          zip: "67156", 
          locationConfidence: LocationConfidenceScoreLabelEnum.Medium
        }
      ]
  ],
  [4, [
        { 
          accessPointId: 106,
          networkType: ProviderNetworkType.Prospect,
          gapsClosed: 8, 
          specialtyImpacted: "General Surgery", 
          countiesImpacted: [
            { key: "17300", countyName: "Geary", stateCode: "KS" },
            { key: "17310", countyName: "Gove", stateCode: "KS" },
            { key: "17320", countyName: "Graham", stateCode: "KS" }
          ],
          address: "209 S. PINE STREET", 
          state: "KS", 
          city: "WICHITA", 
          zip: "67214", 
          locationConfidence: LocationConfidenceScoreLabelEnum.VeryLow
        }
      ]
  ],
  [5, [
        { 
          accessPointId: 107,
          networkType: ProviderNetworkType.Prospect,
          gapsClosed: 1, 
          specialtyImpacted: "Allergy and Inmunology", 
          countiesImpacted: [
            { key: "17330", countyName: "Grant", stateCode: "KS" },
            { key: "17340", countyName: "Gray", stateCode: "KS" }
          ],
          address: "209 S. PINE STREET", 
          state: "KS", 
          city: "WICHITA", 
          zip: "67214", 
          locationConfidence: LocationConfidenceScoreLabelEnum.VeryLow
        }
      ]
  ],
  [6, [
      { 
        accessPointId: 108,
        networkType: ProviderNetworkType.Market,
        gapsClosed: 0, 
        specialtyImpacted: "General Surgery", 
        countiesImpacted: [
          { key: "26996", countyName: "Wright", stateCode: "MO" }
        ],
        address: "209 S. PINE STREET", 
        state: "KS", 
        city: "WICHITA", 
        zip: "67214", 
        locationConfidence: LocationConfidenceScoreLabelEnum.Medium
      }
    ]
  ],
  [7, [
      { 
        accessPointId: 109,
        networkType: ProviderNetworkType.Network,
        gapsClosed: 4, 
        specialtyImpacted: "General Surgery", 
        countiesImpacted: [
          { key: "26996", countyName: "Wright", stateCode: "MO" },
          { key: "26990", countyName: "Vernon", stateCode: "MO" }
        ],
        address: "209 S. PINE STREET", 
        state: "KS", 
        city: "WICHITA", 
        zip: "67214", 
        locationConfidence: LocationConfidenceScoreLabelEnum.High
      }
    ]
  ],
  [8, [
      { 
        accessPointId: 110,
        networkType: ProviderNetworkType.NetNew,
        gapsClosed: 1, 
        specialtyImpacted: "Allergy and Inmunology", 
        countiesImpacted: [
          { key: "26989", countyName: "Texas", stateCode: "MO"}
        ],
        address: "209 S. PINE STREET", 
        state: "KS", 
        city: "WICHITA", 
        zip: "67214", 
        locationConfidence: LocationConfidenceScoreLabelEnum.High
      }
    ]
  ],
  [9, [
      { 
        accessPointId: 111,
        networkType: ProviderNetworkType.Market,
        gapsClosed: 1, 
        specialtyImpacted: "Allergy and Inmunology", 
        countiesImpacted: [
          { key: "26982", countyName: "Scott", stateCode: "MO"},
          { key: "26981", countyName: "Scotland", stateCode: "MO"},
          { key: "26980", countyName: "Schuyler", stateCode: "MO"}
        ],
        address: "209 S. PINE STREET", 
        state: "KS", 
        city: "WICHITA", 
        zip: "67214", 
        locationConfidence: LocationConfidenceScoreLabelEnum.High
      }
    ]
  ],
  [10, [
      { 
        accessPointId: 112,
        networkType: ProviderNetworkType.Market,
        gapsClosed: 1, 
        specialtyImpacted: "Allergy and Inmunology", 
        countiesImpacted: [
          { key: "26840", countyName: "Pulaski", stateCode: "MO"},
          { key: "26821", countyName: "Polk", stateCode: "MO"}
        ],
        address: "209 S. PINE STREET", 
        state: "KS", 
        city: "WICHITA", 
        zip: "67214", 
        locationConfidence: LocationConfidenceScoreLabelEnum.High
      }
    ]
  ],
  [11, [
      { 
        accessPointId: 113,
        networkType: ProviderNetworkType.Network,
        gapsClosed: 1, 
        specialtyImpacted: "Pulmonology", 
        countiesImpacted: [
          { key: "26660", countyName: "Mississippi", stateCode: "MO"},
          { key: "26650", countyName: "Miller", stateCode: "MO"},
          { key: "26631", countyName: "Mercer", stateCode: "MO"}
        ],
        address: "209 S. PINE STREET", 
        state: "KS", 
        city: "WICHITA", 
        zip: "67214", 
        locationConfidence: LocationConfidenceScoreLabelEnum.High
      }
    ]
  ],
  [12, [
      { 
        accessPointId: 114,
        networkType: ProviderNetworkType.Network,
        gapsClosed: 1, 
        specialtyImpacted: "Pulmonology", 
        countiesImpacted: [
          { key: "26220", countyName: "Clark", stateCode: "MO"},
          { key: "26200", countyName: "Chariton", stateCode: "MO"}
        ],
        address: "209 S. PINE STREET", 
        state: "KS", 
        city: "WICHITA", 
        zip: "67214", 
        locationConfidence: LocationConfidenceScoreLabelEnum.High
      }
    ]
  ],
  [13, [
      { 
        accessPointId: 115,
        networkType: ProviderNetworkType.Network,
        gapsClosed: 1, 
        specialtyImpacted: "Pulmonology", 
        countiesImpacted: [
          { key: "26000", countyName: "Adair", stateCode: "MO"},
          { key: "26010", countyName: "Andrew", stateCode: "MO"},
          { key: "26020", countyName: "Atchison", stateCode: "MO"}
        ],
        address: "209 S. PINE STREET", 
        state: "KS", 
        city: "WICHITA", 
        zip: "67214", 
        locationConfidence: LocationConfidenceScoreLabelEnum.High
      }
    ]
  ],
  [14, [
      { 
        accessPointId: 116,
        networkType: ProviderNetworkType.NetNew,
        gapsClosed: 1, 
        specialtyImpacted: "Urology", 
        countiesImpacted: [
          { key: "17080", countyName: "Chase", stateCode: "KS" },
          { key: "17100", countyName: "Cherokee", stateCode: "KS" },
          { key: "17160", countyName: "Comanche", stateCode: "KS" }
        ],
        address: "1121 STONERIDGE DR", 
        state: "KS", 
        city: "LAWRENCE", 
        zip: "66049", 
        locationConfidence: LocationConfidenceScoreLabelEnum.High
      }
    ]
  ],
  [15, [
      { 
        accessPointId: 117,
        networkType: ProviderNetworkType.NetNew,
        gapsClosed: 1, 
        specialtyImpacted: "Urology", 
        countiesImpacted: [
          { key: "17500", countyName: "Lane", stateCode: "KS" },
          { key: "17510", countyName: "Leavenworth", stateCode: "KS" },
          { key: "17520", countyName: "Lincoln", stateCode: "KS" }
        ],
        address: "SUITE 210, MAIL STOP 5003", 
        state: "KS", 
        city: "WESTWOOD", 
        zip: "66205", 
        locationConfidence: LocationConfidenceScoreLabelEnum.High
      }
    ]
  ],
  [16, [
      { 
        accessPointId: 118,
        networkType: ProviderNetworkType.Prospect,
        gapsClosed: 1, 
        specialtyImpacted: "Urology", 
        countiesImpacted: [
          { key: "17610", countyName: "Mitchell", stateCode: "KS" },
          { key: "17620", countyName: "Montgomery", stateCode: "KS" },
          { key: "17630", countyName: "Morris", stateCode: "KS" }
        ],
        address: "800 BARKER DR", 
        state: "KS", 
        city: "OSWEGO", 
        zip: "67356", 
        locationConfidence: LocationConfidenceScoreLabelEnum.VeryLow
      }
    ]
  ],
  [17, [
      { 
        accessPointId: 119,
        networkType: ProviderNetworkType.Prospect,
        gapsClosed: 1, 
        specialtyImpacted: "Cardiology", 
        countiesImpacted: [
          { key: "17700", countyName: "Osborne", stateCode: "KS" },
          { key: "17710", countyName: "Ottawa", stateCode: "KS" },
          { key: "17730", countyName: "Phillips", stateCode: "KS" }
        ],
        address: "800 BARKER DR", 
        state: "KS", 
        city: "OSWEGO", 
        zip: "67356", 
        locationConfidence: LocationConfidenceScoreLabelEnum.VeryLow
      }
    ]
  ],
  [18, [
      { 
        accessPointId: 120,
        networkType: ProviderNetworkType.Prospect,
        gapsClosed: 5, 
        specialtyImpacted: "Diagnostic Radiology", 
        countiesImpacted: [
          { key: "17700", countyName: "Osborne", stateCode: "KS" },
          { key: "17710", countyName: "Ottawa", stateCode: "KS" },
          { key: "17730", countyName: "Phillips", stateCode: "KS" }
        ],
        address: "1005 N LINCOLN AVE", 
        state: "KS", 
        city: "BELOIT", 
        zip: "67420", 
        locationConfidence: LocationConfidenceScoreLabelEnum.VeryLow
      }
    ]
  ],
  [19, [
      { 
        accessPointId: 121,
        networkType: ProviderNetworkType.Network,
        gapsClosed: 2, 
        specialtyImpacted: "Acute Inpatient Hospitals", 
        countiesImpacted: [
          { key: "17780", countyName: "Republic", stateCode: "KS" },
          { key: "17790", countyName: "Rice", stateCode: "KS" },
          { key: "17810", countyName: "Rooks", stateCode: "KS" }
        ],
        address: "1005 N LINCOLN AVE", 
        state: "KS", 
        city: "BELOIT", 
        zip: "67420", 
        locationConfidence: LocationConfidenceScoreLabelEnum.VeryLow
      }
    ]
  ],
  [20, [
      { 
        accessPointId: 122,
        networkType: ProviderNetworkType.Network,
        gapsClosed: 2, 
        specialtyImpacted: "Acute Inpatient Hospitals", 
        countiesImpacted: [
          { key: "17790", countyName: "Rice", stateCode: "KS" },
          { key: "17810", countyName: "Rooks", stateCode: "KS" }
        ],
        address: "1005 N LINCOLN AVE", 
        state: "KS", 
        city: "BELOIT", 
        zip: "67420", 
        locationConfidence: LocationConfidenceScoreLabelEnum.VeryLow
      }
    ]
  ],
  [21, [
      { 
        accessPointId: 122,
        networkType: ProviderNetworkType.Prospect,
        gapsClosed: 2, 
        specialtyImpacted: "Primary Care", 
        countiesImpacted: [
          { key: "26820", countyName: "Platte", stateCode: "MO"},
          { key: "26821", countyName: "Polk", stateCode: "MO"},
          { key: "26840", countyName: "Pulaski", stateCode: "MO"}
        ],
        address: "2711 S ROUSE ST", 
        state: "KS", 
        city: "PITTSBURG", 
        zip: "66762", 
        locationConfidence: LocationConfidenceScoreLabelEnum.High
      }
    ]
  ]
 

]);

const mockedProviderHubServicingProviders: ProviderHubServicingProviders[] = [
  { 
    servicingProviderId: 1, 
    providerName: "Provider 1", 
    npi: "NPI: 123", 
    networkTypes: [ProviderNetworkType.Prospect, ProviderNetworkType.NetNew],
    gapsClosed: 1,
    specialtiesImpacted: [
      { key: "040", name: "Acute Inpatient Hospitals" },
      { key: "007", name: "Allergy and Immunology" },
      { key: "042", name: "Cardiac Catheterization Services" }
    ],
    countiesImpacted: [
      { key: "17000", countyName: "Allen", stateCode: "KS" },
      { key: "17020", countyName: "Atchison", stateCode: "KS" },
      { key: "17030", countyName: "Barber", stateCode: "KS" }
    ], 
    accessPointCount: 2, 
    primaryContractEntity: 'Contract Entity 1', 
    contractEntityProviders: 100
  },
  { 
    servicingProviderId: 2, 
    providerName: "UnitedHealth Group", 
    npi: "NPI: 987", 
    networkTypes: [ProviderNetworkType.Prospect, ProviderNetworkType.NetNew, ProviderNetworkType.Market],
    gapsClosed: 1,
    specialtiesImpacted: [
      { key: "041", name: "Cardiac Surgery Program" },
      { key: "035", name: "Cardiothoracic Surgery" },
      { key: "010", name: "Chiropractor" },
      { key: "008", name: "Cardiology" }
    ],
    countiesImpacted: [
      { key: "17060", countyName: "Brown", stateCode: "KS" }
    ], 
    accessPointCount: 7, 
    primaryContractEntity: 'UnitedHealth Group', 
    contractEntityProviders: 85
  },
  { 
    servicingProviderId: 3, 
    providerName: "Provider 3", 
    npi: "NPI: 108", 
    networkTypes: [ProviderNetworkType.Market, ProviderNetworkType.NetNew],
    gapsClosed: 2,
    specialtiesImpacted: [
      { key: "011", name: "Dermatology" },
      { key: "047", name: "Diagnostic Radiology" }
    ],
    countiesImpacted: [
      { key: "17090", countyName: "Chautauqua", stateCode: "KS" },
      { key: "17100", countyName: "Cherokee", stateCode: "KS" },
      { key: "17110", countyName: "Cheyenne", stateCode: "KS" }
    ], 
    accessPointCount: 1, 
    primaryContractEntity: 'Contract Entity 3', 
    contractEntityProviders: 14
  },
  { 
    servicingProviderId: 4,
    providerName: "Centene Corp", 
    npi: "NPI: 111", 
    networkTypes: [ProviderNetworkType.Market],
    gapsClosed: 5, 
    specialtiesImpacted: [
      { key: "012", name: "Endocrinology" },
      { key: "013", name: "ENT/Otolaryngology" }
    ],
    countiesImpacted: [
      { key: "17300", countyName: "Geary", stateCode: "KS" },
      { key: "17310", countyName: "Gove", stateCode: "KS" },
      { key: "17320", countyName: "Graham", stateCode: "KS" }
    ], 
    accessPointCount: 5, 
    primaryContractEntity: 'CVS Health', 
    contractEntityProviders: 14
  },
  { 
    servicingProviderId: 5, 
    providerName: "Provider 5", 
    npi: "NPI: 555", 
    networkTypes: [ProviderNetworkType.Prospect, ProviderNetworkType.NetNew],
    gapsClosed: 8,
    specialtiesImpacted: [
      { key: "014", name: "Gastroenterology" },
      { key: "015", name: "General Surgery" }
    ],
    countiesImpacted: [
      { key: "17330", countyName: "Grant", stateCode: "KS" },
      { key: "17340", countyName: "Gray", stateCode: "KS" }
    ], 
    accessPointCount: 2, 
    primaryContractEntity: 'Pfizer', 
    contractEntityProviders: 8
  },
  { 
    servicingProviderId: 6, 
    providerName: "Kaiser Foundation", 
    npi: "NPI: 987", 
    networkTypes: [ProviderNetworkType.Prospect, ProviderNetworkType.NetNew, ProviderNetworkType.Market],
    gapsClosed: 7, 
    specialtiesImpacted: [
     { key: "016", name: "Gynecology, OB/GYN" }
    ],
    countiesImpacted: [
      { key: "26996", countyName: "Wright", stateCode: "MO" }
    ], 
    accessPointCount: 7, 
    primaryContractEntity: 'Contract Entity 6', 
    contractEntityProviders: 85
  },
  { 
    servicingProviderId: 7, 
    providerName: "Provider 7", 
    npi: "NPI: 555", 
    networkTypes: [ProviderNetworkType.Market, ProviderNetworkType.NetNew],
    gapsClosed: 12,
    specialtiesImpacted: [
      { key: "017", name: "Infectious Diseases" },
      { key: "052", name: "Inpatient Psychiatric Facility Services" }
    ],
    countiesImpacted: [
      { key: "26996", countyName: "Wright", stateCode: "MO" },
      { key: "26990", countyName: "Vernon", stateCode: "MO" }
    ], 
    accessPointCount: 2, 
    primaryContractEntity: 'McKesson Corporation', 
    contractEntityProviders: 8
  },
  { 
    servicingProviderId: 8, 
    providerName: "Molina Healthcare Inc.", 
    npi: "NPI: 987", 
    networkTypes: [ProviderNetworkType.Prospect, ProviderNetworkType.NetNew, ProviderNetworkType.Market],
    gapsClosed: 2,
    specialtiesImpacted: [
      { key: "048", name: "Mammography" },
      { key: "018", name: "Nephrology" }
    ],
    countiesImpacted: [
      { key: "26989", countyName: "Texas", stateCode: "MO"}
    ], 
    accessPointCount: 7, 
    primaryContractEntity: 'Contract Entity 8', 
    contractEntityProviders: 85
  },
  { 
    servicingProviderId: 9, 
    providerName: "Provider 9", 
    npi: "NPI: 123", 
    networkTypes: [ProviderNetworkType.Prospect, ProviderNetworkType.NetNew],
    gapsClosed: 6,
    specialtiesImpacted: [
      { key: "019", name: "Neurology" },
      { key: "020", name: "Neurosurgery" }
    ],
    countiesImpacted: [
      { key: "26982", countyName: "Scott", stateCode: "MO"},
      { key: "26981", countyName: "Scotland", stateCode: "MO"},
      { key: "26980", countyName: "Schuyler", stateCode: "MO"}
    ], 
    accessPointCount: 2, 
    primaryContractEntity: 'Centene Corporation', 
    contractEntityProviders: 100
  },
  { 
    servicingProviderId: 10, 
    providerName: "Caresource", 
    npi: "NPI: 555", 
    networkTypes: [ProviderNetworkType.Market, ProviderNetworkType.NetNew],
    gapsClosed: 1,
    specialtiesImpacted: [
      { key: "050", name: "Occupational Therapy" },
      { key: "021", name: "Oncology - Medical, Surgical" }
    ],
    countiesImpacted: [
      { key: "26840", countyName: "Pulaski", stateCode: "MO"},
      { key: "26821", countyName: "Polk", stateCode: "MO"}
    ], 
    accessPointCount: 2, 
    primaryContractEntity: 'Contract Entity 10', 
    contractEntityProviders: 8 
  },
  { 
    servicingProviderId: 11, 
    providerName: "Metropolitan", 
    npi: "NPI: 123", 
    networkTypes: [ProviderNetworkType.Market, ProviderNetworkType.NetNew, ProviderNetworkType.Prospect],
    gapsClosed: 4,
    specialtiesImpacted: [
      { key: "022", name: "Oncology - Radiation/Radiation Oncology" },
      { key: "023", name: "Ophthalmology" }
    ],
    countiesImpacted: [
      { key: "26660", countyName: "Mississippi", stateCode: "MO"},
      { key: "26650", countyName: "Miller", stateCode: "MO"},
      { key: "26631", countyName: "Mercer", stateCode: "MO"}
    ], 
    accessPointCount: 2, 
    primaryContractEntity: 'Humana', 
    contractEntityProviders: 100 
  },
  { 
    servicingProviderId: 12, 
    providerName: "Provider 12", 
    npi: "NPI: 555", 
    networkTypes: [ProviderNetworkType.NetNew],
    gapsClosed: 5,
    specialtiesImpacted: [
      { key: "025", name: "Orthopedic Surgery" }
    ],
    countiesImpacted: [
      { key: "26220", countyName: "Clark", stateCode: "MO"},
      { key: "26200", countyName: "Chariton", stateCode: "MO"}
    ], 
    accessPointCount: 2, 
    primaryContractEntity: 'Contract Entity 12', 
    contractEntityProviders: 8 
  },
  { 
    servicingProviderId: 13, 
    providerName: "Kansas University Physicians Inc", 
    npi: "NPI: 123", 
    networkTypes: [ProviderNetworkType.Prospect, ProviderNetworkType.NetNew, ProviderNetworkType.Market],
    gapsClosed: 9,
    specialtiesImpacted: [
      { key: "057", name: "Outpatient Infusion/Chemotherapy" }
    ],
    countiesImpacted: [
      { key: "26000", countyName: "Adair", stateCode: "MO"},
      { key: "26010", countyName: "Andrew", stateCode: "MO"},
      { key: "26020", countyName: "Atchison", stateCode: "MO"}
    ], 
    accessPointCount: 2, 
    primaryContractEntity: 'Merck & Co', 
    contractEntityProviders: 100 
  },
  { 
    servicingProviderId: 14, 
    providerName: "Provider 14", 
    npi: "NPI: 555", 
    networkTypes: [ProviderNetworkType.Prospect],
    gapsClosed: 10,
    specialtiesImpacted: [
      { key: "026", name: "Physiatry, Rehabilitative Medicine" },
      { key: "049", name: "Physical Therapy" }
    ],
    countiesImpacted: [
      { key: "17080", countyName: "Chase", stateCode: "KS" },
      { key: "17100", countyName: "Cherokee", stateCode: "KS" },
      { key: "17160", countyName: "Comanche", stateCode: "KS" }
    ], 
    accessPointCount: 2, 
    primaryContractEntity: 'Contract Entity 14', 
    contractEntityProviders: 8 
  },
  { 
    servicingProviderId: 15, 
    providerName: "Multiple Group Affiliations", 
    npi: "NPI: 123", 
    networkTypes: [ProviderNetworkType.Prospect, ProviderNetworkType.NetNew],
   
    gapsClosed: 2,
    specialtiesImpacted: [
      { key: "027", name: "Plastic Surgery" },
      { key: "028", name: "Podiatry" }
    ],
    countiesImpacted: [
      { key: "17500", countyName: "Lane", stateCode: "KS" },
      { key: "17510", countyName: "Leavenworth", stateCode: "KS" },
      { key: "17520", countyName: "Lincoln", stateCode: "KS" }
    ], 
    accessPointCount: 2, 
    primaryContractEntity: 'TriWest Healthcare Alliance', 
    contractEntityProviders: 100 
  },
  { 
    servicingProviderId: 16, 
    providerName: "Provider 16", 
    npi: "NPI: 108", 
    networkTypes: [ProviderNetworkType.Prospect, ProviderNetworkType.Market],
    gapsClosed: 1,
    specialtiesImpacted: [
      { key: "S03", name: "Primary Care" },
      { key: "029", name: "Psychiatry" }
    ],
    countiesImpacted: [
      { key: "17610", countyName: "Mitchell", stateCode: "KS" },
      { key: "17620", countyName: "Montgomery", stateCode: "KS" },
      { key: "17630", countyName: "Morris", stateCode: "KS" }
    ], 
    accessPointCount: 1, 
    primaryContractEntity: 'Contract Entity 16', 
    contractEntityProviders: 14 
  },
  { 
    servicingProviderId: 17, 
    providerName: "WellCare", 
    npi: "NPI: 123", 
    networkTypes: [ProviderNetworkType.NetNew, ProviderNetworkType.Market],
    gapsClosed: 3,
    specialtiesImpacted: [
      { key: "030", name: "Pulmonology" },
      { key: "031", name: "Rheumatology" }
    ],
    countiesImpacted: [
      { key: "17700", countyName: "Osborne", stateCode: "KS" },
      { key: "17710", countyName: "Ottawa", stateCode: "KS" },
      { key: "17730", countyName: "Phillips", stateCode: "KS" }
    ], 
    accessPointCount: 2, 
    primaryContractEntity: 'GlaxoSmithKline (GSK)', 
    contractEntityProviders: 100 
  },
  { 
    servicingProviderId: 18, 
    providerName: "Provider 18", 
    npi: "NPI: 108", 
    networkTypes: [ProviderNetworkType.NetNew, ProviderNetworkType.Prospect],
    gapsClosed: 10,
    specialtiesImpacted: [
      { key: "046", name: "Skilled Nursing Facilities" },
      { key: "031", name: "Rheumatology" }
    ],
    countiesImpacted: [
      { key: "17700", countyName: "Osborne", stateCode: "KS" },
      { key: "17710", countyName: "Ottawa", stateCode: "KS" },
      { key: "17730", countyName: "Phillips", stateCode: "KS" }
    ], 
    accessPointCount: 1, 
    primaryContractEntity: 'Food and Drug Administration', 
    contractEntityProviders: 14 
  },
  { 
    servicingProviderId: 19, 
    providerName: "Commcare Pharmacy", 
    npi: "NPI: 123", 
    networkTypes: [ProviderNetworkType.Prospect, ProviderNetworkType.NetNew, ProviderNetworkType.Market],
    gapsClosed: 2,
    specialtiesImpacted: [
      { key: "046", name: "Speech Therapy" },
      { key: "045", name: "Surgical Services (Outpatient or ASC)" }
    ],
    countiesImpacted: [
      { key: "17780", countyName: "Republic", stateCode: "KS" },
      { key: "17790", countyName: "Rice", stateCode: "KS" },
      { key: "17810", countyName: "Rooks", stateCode: "KS" }
    ], 
    accessPointCount: 2, 
    primaryContractEntity: 'Contract Entity 19', 
    contractEntityProviders: 100 
  },
  { 
    servicingProviderId: 20, 
    providerName: "Provider 20", 
    npi: "NPI: 555", 
    networkTypes: [ProviderNetworkType.Prospect, ProviderNetworkType.NetNew, ProviderNetworkType.Market],
    gapsClosed: 4,
    specialtiesImpacted: [
      { key: "046", name: "Speech Therapy" }
    ],
    countiesImpacted: [
      { key: "17790", countyName: "Rice", stateCode: "KS" },
      { key: "17810", countyName: "Rooks", stateCode: "KS" }
    ], 
    accessPointCount: 2, 
    primaryContractEntity: 'Contract Entity 20', 
    contractEntityProviders: 8 
  },
  { 
    servicingProviderId: 21, 
    providerName: "RTI Surgical", 
    npi: "NPI: 108", 
    networkTypes: [ProviderNetworkType.NetNew, ProviderNetworkType.Market],
    gapsClosed: 2,
    specialtiesImpacted: [
      { key: "033", name: "Urology" },
      { key: "034", name: "Vascular Surgery" }
    ],
    countiesImpacted: [
      { key: "26820", countyName: "Platte", stateCode: "MO"},
      { key: "26821", countyName: "Polk", stateCode: "MO"},
      { key: "26840", countyName: "Pulaski", stateCode: "MO"}
    ], 
    accessPointCount: 1, 
    primaryContractEntity: 'National Institutes of Health', 
    contractEntityProviders: 14 
  },
  
];
/* eslint-enable sort-keys */

